import { plural } from '@/lib/plural';
import { parseTelegramHtml, problemText, visibleText } from './telegramHtml';
export const LOCALES = ['ru', 'en', 'hy', 'tr'];
export const MAX_BUTTONS = 5;
/** Room kept for the line the bot adds under a text with a gift («*Подарок можно получить до: …»). */
export const GIFT_LINE_RESERVE = 60;
export const emptyTranslation = (language) => ({
    language,
    text: '',
    subject: '',
    giftButton: '',
    buttons: [],
});
const str = (n) => (n == null ? '' : String(n));
/** A saved mailing's form, or a new one's: Telegram, running subscriptions, everyone, in Russian. */
export function mailingDraft(m) {
    if (!m) {
        return {
            name: '',
            telegram: true,
            email: false,
            subscription: 'active',
            daysFrom: '',
            daysTo: '',
            payment: 'any',
            eventIds: [],
            limit: '',
            order: 'random',
            media: null,
            giftId: null,
            translations: [emptyTranslation('ru')],
        };
    }
    return {
        name: m.name,
        telegram: m.channel_telegram,
        email: m.channel_email,
        subscription: m.audience_subscription,
        daysFrom: str(m.days_from),
        daysTo: str(m.days_to),
        payment: m.audience_payment,
        eventIds: Array.isArray(m.event_ids) ? [...new Set(m.event_ids)] : [],
        limit: str(m.recipient_limit),
        order: m.limit_order,
        media: m.media_type && m.media_url ? { media_type: m.media_type, url: m.media_url } : null,
        giftId: m.gift_id,
        translations: m.translations.map((t) => ({
            language: t.language,
            text: t.text_html,
            subject: t.email_subject ?? '',
            giftButton: t.gift_button_text ?? '',
            buttons: t.buttons.map((b) => ({ text: b.text, url: b.url })),
        })),
    };
}
const COPY = ' (копия)';
/** A finished mailing as a new draft: the same settings and texts. */
export function copyDraft(m) {
    const d = mailingDraft(m);
    return { ...d, name: `${d.name.slice(0, 128 - COPY.length)}${COPY}` };
}
const bound = (d, v) => d.subscription === 'all' || v.trim() === '' ? null : Number(v);
const orNull = (v) => v.trim() || null;
/** The POST body. */
export function mailingBody(d) {
    return {
        name: d.name.trim(),
        channel_telegram: d.telegram,
        channel_email: d.email,
        audience_subscription: d.subscription,
        days_from: bound(d, d.daysFrom),
        days_to: bound(d, d.daysTo),
        audience_payment: d.payment,
        event_ids: [...new Set(d.eventIds)],
        recipient_limit: d.limit.trim() === '' ? null : Number(d.limit),
        limit_order: d.order,
        media_type: d.media?.media_type ?? null,
        media_url: d.media?.url ?? null,
        gift_id: d.giftId,
        translations: d.translations.map((t) => ({
            language: t.language,
            text_html: t.text.trim(),
            email_subject: orNull(t.subject),
            buttons: t.buttons.map((b) => ({ text: b.text.trim(), url: b.url.trim() })),
            gift_button_text: orNull(t.giftButton),
        })),
    };
}
/** The PATCH: only what changed; the media goes as a pair, translations whole. */
export function mailingPatch(m, d) {
    const before = mailingBody(mailingDraft(m));
    const after = mailingBody(d);
    const patch = {};
    for (const key of Object.keys(after)) {
        if (JSON.stringify(after[key]) !== JSON.stringify(before[key]))
            patch[key] = after[key];
    }
    if ('media_type' in patch || 'media_url' in patch) {
        patch.media_type = after.media_type;
        patch.media_url = after.media_url;
    }
    return patch;
}
const INT = /^\d+$/;
const HTTPS = /^https:\/\/\S+$/i;
const hasControl = (v) => [...v].some((c) => {
    const code = c.charCodeAt(0);
    return code < 32 || code === 127;
});
/** A one-line field: empty (its message when required), control characters, too long. */
function line(v, max, required) {
    const t = v.trim();
    if (!t)
        return required;
    if (hasControl(t))
        return 'Одной строкой';
    if (t.length > max)
        return `Не длиннее ${max} знаков`;
    return undefined;
}
/** How long the text is in Telegram, with the gift line, and how long it may be. */
export function textBudget(d, text) {
    return {
        used: visibleText(text.trim()).length + (d.giftId ? GIFT_LINE_RESERVE : 0),
        limit: d.media ? 1024 : 4096,
    };
}
export function mailingErrors(d) {
    const e = { byLanguage: {} };
    e.name = line(d.name, 128, 'Нужно название');
    if (!d.telegram && !d.email)
        e.channels = 'Нужен хотя бы один канал';
    if (d.subscription !== 'all') {
        const from = d.daysFrom.trim();
        const to = d.daysTo.trim();
        if ((from && !INT.test(from)) || (to && !INT.test(to)))
            e.days = 'Целое число дней, от 0';
        else if (from && to && Number(from) > Number(to))
            e.days = '«От» больше, чем «до»';
    }
    const limit = d.limit.trim();
    if (limit && !(INT.test(limit) && Number(limit) > 0))
        e.limit = 'Целое число больше 0';
    if (d.translations.length === 0)
        e.languages = 'Нужен хотя бы один язык';
    for (const t of d.translations) {
        const te = { buttons: [] };
        if (!t.text.trim()) {
            te.text = 'Нужен текст';
        }
        else {
            const problem = parseTelegramHtml(t.text).problems[0];
            const { used, limit: max } = textBudget(d, t.text);
            if (problem)
                te.text = problemText(problem);
            else if (used > max) {
                const over = used - max;
                te.text = `Длиннее на ${over} ${plural(over, 'знак', 'знака', 'знаков')} (можно ${max}${d.giftId ? ' с подписью подарка' : ''})`;
            }
        }
        if (d.email)
            te.subject = line(t.subject, 200, 'Нужна тема письма');
        if (d.giftId)
            te.giftButton = line(t.giftButton, 64, 'Нужен текст кнопки подарка');
        te.buttons = t.buttons.map((b) => {
            const url = b.url.trim();
            const be = {
                text: line(b.text, 64, 'Нужен текст'),
                url: !HTTPS.test(url)
                    ? 'Адрес с https://'
                    : url.length > 2048
                        ? 'Слишком длинный адрес'
                        : undefined,
            };
            return be.text || be.url ? be : null;
        });
        if (te.text || te.subject || te.giftButton || te.buttons.some(Boolean))
            e.byLanguage[t.language] = te;
    }
    return e;
}
export const hasErrors = (e) => !!(e.name || e.channels || e.days || e.limit || e.languages) ||
    Object.keys(e.byLanguage).length > 0;
